import { useEffect, useRef, useState } from "react";
import { usePageMeta } from "../usePageMeta";

interface UnsubscribePageProps {
  apiUrl: string;
  token: string;
}

const PAGE_TITLE = "Unsubscribe | Embroidery Text";
const PAGE_DESCRIPTION = "Unsubscribe from Embroidery Text emails.";
const SUPPORT_ADDRESS = "support@embroiderytext.com";

/**
 * Where a campaign email's unsubscribe link lands: /unsubscribe/<token>.
 *
 * Unsubscribes on load, no login and no button, as the link promises
 * (app/routers/campaigns.py). It takes the address off every future
 * campaign; emails the person asks for themselves, like a design sent to
 * their inbox, still arrive. No analytics here: the server reports
 * email_campaign_unsubscribed itself, without the address.
 */
export function UnsubscribePage({ apiUrl, token }: UnsubscribePageProps) {
  usePageMeta({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: "/unsubscribe" });
  const [state, setState] = useState<"working" | "done" | "test" | "missing" | "error">("working");
  // StrictMode runs effects twice in development; the request is idempotent,
  // but there's no reason to make it twice.
  const asked = useRef(false);

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    fetch(`${apiUrl}/api/unsubscribe/${encodeURIComponent(token)}`, { method: "POST" })
      .then(async (response) => {
        if (response.status === 404) return setState("missing");
        if (!response.ok) return setState("error");
        const data = await response.json();
        setState(data.test ? "test" : "done");
      })
      .catch(() => setState("error"));
  }, [apiUrl, token]);

  const contact = <a href={`mailto:${SUPPORT_ADDRESS}`}>{SUPPORT_ADDRESS}</a>;

  if (state === "working") return <p aria-busy="true">Unsubscribing…</p>;

  if (state === "missing") {
    return (
      <article>
        <h2>Link not recognised</h2>
        <p>
          This unsubscribe link isn't valid. Check it against your email, or
          write to {contact} and we'll take you off the list.
        </p>
      </article>
    );
  }

  if (state === "error") {
    return (
      <article>
        <h2>Something went wrong</h2>
        <p>
          We couldn't unsubscribe you just now. Please try the link again, or
          write to {contact} and we'll take you off the list.
        </p>
      </article>
    );
  }

  return (
    <article>
      <h2>You're unsubscribed</h2>
      {state === "test" && (
        <p>
          <mark>This was a test email, so nothing was changed.</mark>
        </p>
      )}
      <p>
        You won't get any more announcements or updates from Embroidery Text.
      </p>
      <p>
        <small>
          Emails you ask for yourself, like a design sent to your inbox, will
          still arrive. Changed your mind? Write to {contact}.
        </small>
      </p>
    </article>
  );
}
